import { useState } from 'react'
import { useClientPortalAssistantOwner } from '@/hooks/useClientPortalAssistant'

const storageKey = (entityId: string, userId: string) =>
  `portal-benny:session:${entityId}:${userId}`

function readSession(key: string | null): string | undefined {
  if (!key) return undefined
  try {
    return sessionStorage.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

function writeSession(key: string | null, sessionId: string | undefined) {
  if (!key) return
  try {
    if (sessionId) sessionStorage.setItem(key, sessionId)
    else sessionStorage.removeItem(key)
  } catch {
    // Storage can be blocked; the popover just starts a new conversation.
  }
}

/**
 * The conversation the top-bar popover keeps open, remembered for this tab
 * per client AND per signed-in user, so a teammate signing in on the same
 * browser never lands in someone else's thread. The top bar remounts with
 * every portal page, which is why this lives in storage and not in state.
 */
export function usePortalBennySession(entityId: string | undefined) {
  const userId = useClientPortalAssistantOwner()
  const key = entityId && userId ? storageKey(entityId, userId) : null
  const [state, setState] = useState(() => ({
    key,
    sessionId: readSession(key),
  }))
  // Re-read when the client or the signed-in user changes.
  if (state.key !== key) {
    setState({ key, sessionId: readSession(key) })
  }
  const setSessionId = (sessionId: string | undefined) => {
    writeSession(key, sessionId)
    setState({ key, sessionId })
  }
  return [
    state.key === key ? state.sessionId : undefined,
    setSessionId,
  ] as const
}
